/**
 * Subscription map. "What will my family lose if I die?"
 * CRUD + monthly total + critical-impact counter.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, SUBSCRIPTION_CATEGORIES, FAMILY_IMPACT, formatINR, toPaise, monthlySubsTotal, criticalSubsCount } from '../types.ts';
import type { FamilyImpact, Subscription, SubscriptionCategory, SubscriptionId } from '../types.ts';
import { sha256 } from '../chassis/crypto.ts';

const TPL = /* html */ `
<style>
.totals {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}
.totals-cell .lab { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--ink-faint); }
.totals-cell .num { font-family: var(--display); font-size: clamp(24px, 4vw, 32px); color: var(--indigo); font-weight: 600; }
</style>
<section class="frame">
  <h2>Subscription map <span class="deva">सदस्यताएँ</span></h2>
  <div class="kicker">What your family must keep, what they should cancel</div>
  <div class="totals">
    <div class="totals-cell">
      <div class="lab">monthly burn</div>
      <div class="num" id="monthly">—</div>
    </div>
    <div class="totals-cell">
      <div class="lab">critical to keep</div>
      <div class="num" id="critical">0</div>
    </div>
  </div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <div class="row">
      <label class="field">Service
        <input id="svc" type="text" required placeholder="Netflix, AWS, GoDaddy" />
      </label>
      <label class="field">Monthly (₹)
        <input id="mon" type="number" min="0" step="10" placeholder="649" required />
      </label>
    </div>
    <div class="row">
      <label class="field">Category
        <select id="cat">
          ${SUBSCRIPTION_CATEGORIES.map((c) => `<option value="${c}">${c}</option>`).join('')}
        </select>
      </label>
      <label class="field">Family impact
        <select id="impact">
          ${FAMILY_IMPACT.map((i) => `<option value="${i}">${i.replace(/_/g, ' ')}</option>`).join('')}
        </select>
      </label>
    </div>
    <div class="row">
      <label class="field">Cancellation URL
        <input id="url" type="url" placeholder="https://..." />
      </label>
      <label class="field">Billing email
        <input id="email" type="email" placeholder="optional, hashed" />
      </label>
    </div>
    <label class="field">Notes
      <input id="notes" type="text" placeholder="e.g. mom's iCloud photos backup, do not cancel" />
    </label>
    <div class="btn-row">
      <button type="submit" class="btn">Add subscription</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatSubscriptionMap extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      const monthly = root.getElementById('monthly');
      const critical = root.getElementById('critical');
      if (monthly) monthly.textContent = formatINR(monthlySubsTotal(v));
      if (critical) critical.textContent = String(criticalSubsCount(v));
      if (!list) return;
      if (v.subscriptions.length === 0) {
        list.innerHTML = `<div class="list-empty">No subscriptions recorded.</div>`;
        return;
      }
      list.innerHTML = v.subscriptions.map((s) => {
        const cls = s.family_impact === 'critical' ? 'crimson' : s.family_impact === 'none' ? 'leaf' : '';
        return `<div class="list-row ${cls}">
          <div>
            <div class="title">${escapeHtml(s.service)} <span class="pill ${cls}">${s.family_impact.replace(/_/g, ' ')}</span></div>
            <div class="sub">${s.category} · ${formatINR(s.monthly_paise)}/mo${s.notes ? ' · ' + escapeHtml(s.notes.slice(0, 80)) : ''}</div>
          </div>
          <button class="btn tiny ghost" data-del="${s.id}">Remove</button>
        </div>`;
      }).join('');
      list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
        b.addEventListener('click', () => {
          const id = b.dataset['del'] as SubscriptionId;
          void store.mutate((d) => { d.subscriptions = d.subscriptions.filter((x) => x.id !== id); });
        });
      });
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const svc = (this.#shadow.getElementById('svc') as HTMLInputElement).value.trim();
      const mon = Number((this.#shadow.getElementById('mon') as HTMLInputElement).value);
      const cat = (this.#shadow.getElementById('cat') as HTMLSelectElement).value as SubscriptionCategory;
      const impact = (this.#shadow.getElementById('impact') as HTMLSelectElement).value as FamilyImpact;
      const url = (this.#shadow.getElementById('url') as HTMLInputElement).value.trim();
      const email = (this.#shadow.getElementById('email') as HTMLInputElement).value.trim();
      const notes = (this.#shadow.getElementById('notes') as HTMLInputElement).value.trim();
      if (!svc || !Number.isFinite(mon)) return;
      const billing_email_hash = email ? await sha256(email) : '';
      const sub: Subscription = {
        id: newId<SubscriptionId>(),
        service: svc,
        monthly_paise: toPaise(mon),
        billing_email_hash,
        cancellation_url: url,
        category: cat,
        family_impact: impact,
        notes,
        created_at: new Date().toISOString(),
      };
      await store.mutate((d) => { d.subscriptions.push(sub); });
      form.reset();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-subscription-map', WasiyatSubscriptionMap);
